import PropTypes from 'prop-types';
import { Fragment } from 'react';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { useExpanded, useSortBy, useTable } from 'react-table';
import Loader from '@/components/loader/loader-component';
import Styled from './sub-row-component-style';

const columnStyle = { textAlign: 'center', padding: '25px 0' };

/**
 * SubTable component for generic use in project.
 * @param {obj} props - Props of the component.
 * @param {Array} props.columns - Columns of the table.
 * @param {Array} props.data - Data of the table.
 * @param {Function} props.renderRowSubComponent - Function to call when the row is expanded.
 * @param {boolean} props.loading - Loading state of the table.
 * @param {boolean} props.error - Error state of the table.
 * @param {Function} props.onRowClick - Function to call when the row is clicked.
 * @param {string} props.tableTitle - Title of the table.
 * @param {string} props.maxHeight - Max height of the table.
 * @param {string} props.checkMe - Check if the row is expanded.
 * @param {object} props.headerStyle - Style of the header.
 * @param {object} props.rowStyle - Style of the row.
 * @param {object} props.style - Style of the table.
 * @returns {JSX.Element}
 */
const SubRow = ({
  columns,
  data,
  renderRowSubComponent,
  onRowClick,
  tableTitle,
  loading,
  checkMe,
  rowStyle,
}) => {
  const { rows, prepareRow } = useTable(
    { columns, data, manualSortBy: true },
    useSortBy,
    useExpanded,
  );
  const renderSubRow = (row) => {
    if (row.isExpanded) {
      return renderRowSubComponent({ row });
    }
    return null;
  };
  if (loading) {
    return (
      <Styled.SubRow>
        <Styled.Column colSpan={columns.length} style={columnStyle}>
          <Loader margin="0px" loading />
        </Styled.Column>
      </Styled.SubRow>
    );
  }
  return rows.map((row) => {
    prepareRow(row);
    if (checkMe === 'renderRowSubComponent' && row.id.includes('.')) {
      return null;
    }
    return (
      <Fragment key={`row-${tableTitle}`}>
        <Styled.SubRow
          key={`row-${tableTitle}`}
          onClick={
            onRowClick
              ? () => {
                  onRowClick(row);
                }
              : null
          }
          className={onRowClick ? 'clickable' : ''}
          style={rowStyle}
          {...row.getRowProps()}
        >
          {row.cells.map((cell) => {
            if (cell.column.id === 'expander') {
              return (
                <Styled.Column
                  {...cell.getCellProps()}
                  key={`row-${tableTitle}-cell-${tableTitle}`}
                  style={columnStyle}
                >
                  {cell.render('Cell')}
                </Styled.Column>
              );
            }
            return (
              <Styled.Column
                {...cell.getCellProps()}
                colSpan={1}
                key={`row-${tableTitle}-cell-${tableTitle}`}
              >
                {cell.render('Cell')}
              </Styled.Column>
            );
          })}
        </Styled.SubRow>
        {renderSubRow(row)}
      </Fragment>
    );
  });
};

SubRow.propTypes = {
  /**
   * The columns that the table should render. https://react-table.js.org/quickstart#define-columns
   */
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  /**
   * The data that should be rendered on the table rows. https://react-table.js.org/quickstart#define-row-shape
   */
  data: PropTypes.any.isRequired,
  /**
   * Flag if the data is loading, also displays loader on table if true.
   */
  loading: PropTypes.bool,
  /**
   * Flag if there was an error while loading data.
   */
  error: PropTypes.bool,
  /**
   * Query function to refetch the data, incase variables change or pagination changes.
   */
  /**
   * Variables to pass to the refetch query. These get combined with the
   * pagination variables.
   */
  /**
   * An on click event that needs to trigger when a user clicks on a row.
   * If this is passed through there will be a hover on the row as well as the cursor will change.
   */
  onRowClick: PropTypes.func,

  setVariables: PropTypes.func,

  tableTitle: PropTypes.string,
  renderRowSubComponent: PropTypes.func,
  /**
   * MaxHeight of table, defaults to 400
   */
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  checkMe: PropTypes.string,
  headerStyle: PropTypes.shape({}),
  rowStyle: PropTypes.shape({}),
  style: PropTypes.shape({}),
};

SubRow.defaultProps = {
  loading: false,
  error: false,
  onRowClick: undefined,
  maxHeight: '60vh',
  checkMe: '',
  renderRowSubComponent: null,
  headerStyle: null,
  rowStyle: null,
  setVariables: undefined,
  style: undefined,
  tableTitle: '',
};

export default SubRow;
